import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Panel } from '~/eds/src/components/Panel/Panel';
import {
  DeleteAction as DeleteActionComponent,
  DeleteDataType,
} from '~/features/document-bulk-actions/DeleteAction';
import { api } from '~/redux/api/createApi';

import { BaseBulkActionProps } from '../types/bulk-actions';

interface Props extends BaseBulkActionProps {
  onHide: () => void;
  selectedCount: number;
}

export const DeleteActionPanel = ({
  isAllSelected = false,
  onHide,
  searchAPI,
  selectedCount,
  selectedDocumentIds = [],
  query = {},
  onActionCompleted,
}: Props) => {
  const [{ comment, reason }, setDeleteData] = useState<DeleteDataType>({
    reason: null,
    comment: '',
  });
  const { toast } = useToast();
  const intl = useIntl();
  const [bulkDelete, { isLoading }] =
    api.endpoints.searchV2BulkDelete.useMutation();

  const onBulkDelete = async () => {
    try {
      const response = await bulkDelete({
        documentHandlerIds: selectedDocumentIds,
        searchAPI,
        searchQuery: query,
        isAllSelected,
        reason: reason ?? '',
        extraDetails: comment ?? '',
      }).unwrap();
      toast({
        status: 'success',
        message:
          response?.detail ??
          intl.formatMessage({
            id: 'DeleteActionPanel.deleteSuccessMessage',
            defaultMessage: 'Documents have been successfully deleted.',
            description: 'Success message when documents are deleted',
          }),
      });
      onActionCompleted?.();
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'DeleteActionPanel.deleteErrorMessage',
          defaultMessage: 'An error occurred while deleting the documents.',
          description: 'Error message when document deletion fails',
        }),
      });
    }
  };

  // 1. must choose a reason, 2. if reason is 'other', must provide details
  const deleteDisabled = !reason || (reason === 'other' && !comment.trim());

  const footer = {
    actions: [
      {
        text: intl.formatMessage({
          id: 'DeleteActionPanel.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text for delete action',
        }),
        level: 'danger' as const,
        onClick: onBulkDelete,
        disabled: deleteDisabled,
        isLoading,
      },
    ],
  };

  return (
    <Panel
      title={intl.formatMessage(
        {
          id: 'DeleteActionPanel.title',
          defaultMessage:
            'Delete {selectedCount, plural, one {Document} other {Documents}}',
          description:
            'Title for delete panel with pluralization. Example: selectedCount = 5',
        },
        { selectedCount },
      )}
      enableBackdrop
      footer={footer}
      width="m"
      hidden={{ isHidden: false, onHide }}
      position="right"
    >
      <DeleteActionComponent
        comment={comment}
        reason={reason}
        onChange={setDeleteData}
        selectedCount={selectedCount}
        type="document"
      />
    </Panel>
  );
};
